import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Button, Card, Table, TableProps } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import ReportPageHeading from "pages/analytics/ReportPageHeading";
import { getMonthlyStatements } from "hooks/queries/analytics";
import { useBusinessSettings } from "hooks/queries/business-settings";
import { useAuth } from "context/AuthContextProvider";
import { translate } from "shared/languages";
import { formatCurrency } from "shared/utils";
import { saveBlobAsFile } from "shared/utils/download";
import { th, tnum } from "shared/components/TableText";
import { formatStatementNumber } from "shared/utils/statementNumber";
import { localDateOnly } from "shared/utils/dayjs";
import { colors } from "shared/theme/color";
import { isSystemAdmin } from "shared/utils/role";
import { MonthlyStatement } from "shared/types";
import PATHS from "shared/constants/paths";
import { loadReportPdf } from "shared/utils/loadPdfChunk";
import { textPx } from "shared/constants/zoom";

// The tax rate trailing the amount — kept smaller and muted so it reads as
// an annotation, not a second value competing with the headline figure.
const noteStyle = { color: colors.ink3, fontSize: textPx(12), marginLeft: 4 };

const rowKey = (row: MonthlyStatement) => `${row.year}-${row.month}`;
const monthLabel = (row: MonthlyStatement) =>
  localDateOnly(`${row.year}-${String(row.month).padStart(2, "0")}-01`).format("MMM YYYY");

// The studio's own monthly sales + tax rollup — a downloadable document per
// month, not a filterable analytics report (the server already returns every
// month with at least one sale), so this page skips the range picker/Excel
// export shared by the other Sales reports.
const MonthlyStatementPage = () => {
  const { data, isLoading } = getMonthlyStatements();
  const { state: authState } = useAuth();
  // The studio's OWN name for their tax (e.g. "PPN") — matches the Invoice/
  // Receipt PDF and Tax Settings rather than a generic "Pajak" label. Always
  // the CURRENT setting (not a per-purchase snapshot): a tax NAME is a
  // naming convenience, not something that needs point-in-time accuracy the
  // way the rate does, and a studio effectively never renames it.
  const { data: businessSettings } = useBusinessSettings();
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  const businessId = authState.business?.id ?? "";

  const handleDownload = async (row: MonthlyStatement) => {
    const key = rowKey(row);
    setDownloadingKey(key);
    try {
      const { renderReportPdf } = await loadReportPdf();
      const blob = await renderReportPdf({
        slug: "pernyataan-bulanan",
        // Prefer the formal/registered name on official paperwork, same as
        // the Invoice/Receipt PDF — falls back to the trading name.
        studio: authState.business?.legal_name || authState.business?.name || "",
        studioAddress: authState.business?.address,
        npwp: authState.business?.npwp,
        businessId,
        data: row,
        taxLabel: businessSettings?.tax_label,
      });
      saveBlobAsFile(
        blob,
        `Laporan_Rincian_Akun_Bulanan_${row.year}-${String(row.month).padStart(2, "0")}.pdf`,
      );
    } finally {
      setDownloadingKey(null);
    }
  };

  // Data-driven, not businessSettings.tax_enabled: a studio that used tax for
  // a while and later turned it off still has real historical tax to show
  // for those months, so the column stays keyed on whether any month ever
  // actually collected tax — same "each row's own history, never the
  // current setting" rule the server's decomposition already follows.
  const hasAnyTax = (data ?? []).some((row) => row.taxAmount > 0);

  // A studio's statement, so a system admin (no business on their token) has
  // nothing to see here: back to their Bisnis home rather than an empty
  // table (System Review S6). The query above is also disabled for them, so
  // the admin-only endpoint never answers the 401 that used to sign them out.
  if (isSystemAdmin(authState.user?.roles)) {
    return <Navigate to={PATHS.BUSINESS} replace />;
  }

  const columns: TableProps<MonthlyStatement>["columns"] = [
    {
      title: th(translate("pdf.monthly_statement.col_month")),
      key: "month",
      render: (_, row) => <span style={{ fontWeight: 600 }}>{monthLabel(row)}</span>,
    },
    {
      // Same deterministic number printed on the downloaded PDF
      // (formatStatementNumber) — a preview of that citation, not a
      // lookup key (there is no in-app search by this number).
      title: th(translate("pdf.monthly_statement.statement_no")),
      key: "statement_no",
      render: (_, row) => (
        <span style={tnum}>{formatStatementNumber(businessId, row.year, row.month)}</span>
      ),
    },
    ...(hasAnyTax
      ? [
          {
            // The percentage renders per ROW, not in this shared header — a
            // month only has one resolvable rate when every taxed sale that
            // month shared the same rate (row.taxRate), which can differ
            // month to month or be undefined if the studio changed its rate
            // (or mixed taxed and untaxed sales) partway through.
            title: th(businessSettings?.tax_label || translate("pdf.monthly_statement.tax")),
            key: "tax",
            render: (_: unknown, row: MonthlyStatement) => (
              <span style={tnum}>
                {formatCurrency(row.taxAmount)}
                {row.taxRate != null ? (
                  <span style={noteStyle}>({row.taxRate}%)</span>
                ) : (
                  // More than one rate that month: naming them is honest and
                  // checkable (the PDF carries the amount per rate). A single
                  // rate sitting beside untaxed sales still shows nothing,
                  // because "(11%)" next to an aggregate that is not 11% of
                  // anything reads as an error on a document used for filing.
                  (row.taxByRate?.length ?? 0) > 1 && (
                    <span style={noteStyle}>
                      ({row.taxByRate?.map((b) => `${b.rate}%`).join(", ")})
                    </span>
                  )
                )}
              </span>
            ),
          },
        ]
      : []),
    {
      title: th(translate("analyticsPage.class.total_label")),
      dataIndex: "total",
      render: (value: number) => <span style={{ ...tnum, fontWeight: 600 }}>{formatCurrency(value)}</span>,
    },
    {
      key: "download",
      render: (_, row) => (
        <Button
          type="text"
          icon={<DownloadOutlined />}
          loading={downloadingKey === rowKey(row)}
          onClick={() => handleDownload(row)}
        >
          {translate("analyticsPage.export.pdf")}
        </Button>
      ),
    },
  ];
  const columnWidth = `${100 / columns.length}%`;
  columns.forEach((c) => (c.width = columnWidth));

  return (
    <>
      <ReportPageHeading
        title={translate("pdf.monthly_statement.title")}
        backTo={PATHS.SETTINGS}
      />
      <Card styles={{ body: { padding: 18 } }}>
        <Table<MonthlyStatement>
          rowKey={rowKey}
          size="middle"
          loading={isLoading}
          columns={columns}
          dataSource={data ?? []}
          pagination={false}
        />
      </Card>
    </>
  );
};

export default MonthlyStatementPage;
